import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '@/context/ToastContext';
import { useAuth } from '@/context/AuthContext';
import { COUNTRIES } from '@/utils/geoData';
import { CountryCitySelector } from '@/components/CountryCitySelector';
import { CURRENCIES_LIST, ccySym, hasCardPayable } from '@/utils/currencyUtils';
import CurrencyService from '@/services/CurrencyService';
import { calcPriceLimits, ZERO_DEC_CCY, STATIC_RATES_EUR } from '@/utils/tripPriceUtils';
import { canDo } from '@/utils/verifications';
import VerificationGate from '@/components/verification/VerificationGate';
import '@/styles/single-form.css';
import '@/styles/pro-upsell.css';
import { BACKEND_URL } from '@/config';

const API = `${BACKEND_URL}/api`;

// ─── Données statiques ────────────────────────────────────────────────────────

const TRANSPORTS = [
  { id: 'avion',   glyph: '✈️', label: 'Avion' },
  { id: 'voiture', glyph: '🚗', label: 'Voiture' },
  { id: 'bus',     glyph: '🚌', label: 'Bus / Car' },
  { id: 'bateau',  glyph: '⛴️', label: 'Bateau' },
  { id: 'train',   glyph: '🚆', label: 'Train' },
  { id: 'autre',   glyph: '📦', label: 'Autre' },
];

const CATEGORIES = ['Vêtements', 'Électronique', 'Alimentaire', 'Documents', 'Médicaments', 'Cosmétiques', 'Livres', 'Autre'];

const CONDITIONS = [
  { id: 'frag',         label: '🏺 Fragiles acceptés' },
  { id: 'temp',         label: '❄️ Température contrôlée' },
  { id: 'scelle',       label: '🔒 Colis scellés requis' },
  { id: 'ass',          label: '🛡️ Assurance incluse' },
  { id: 'id_check',     label: '🪪 Vérif. identité expéditeur' },
  { id: 'pickup',       label: '🏠 Remise à domicile possible' },
  { id: 'airport_only', label: '✈️ Dépôt aéroport uniquement' },
  { id: 'contactless',  label: '🤝 Sans contact possible' },
  { id: 'trace',        label: '📍 Suivi en temps réel' },
  { id: 'sign',         label: '✍️ Signature à la livraison' },
  { id: 'double_check', label: '🔍 Double vérif. contenu' },
  { id: 'doc_required', label: "📄 Documents d'envoi requis" },
];

const currencySymbol = ccySym;

// ─── Formulaire principal ─────────────────────────────────────────────────────

export default function TripForm() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const toast      = useToast();
  const { user }   = useAuth();
  const { id }     = useParams();
  const isEdit     = !!id;
  const isDuplicate = new URLSearchParams(location.search).get('duplicate') === '1';

  // Capacité et plafond de prix : réservés à l'abonnement PRO (pas au type de compte déclaré)
  const isPro            = !!user?.pro_subscribed;
  const maxWeightAllowed = isPro ? 1500 : 250;

  const [loading,      setLoading]      = useState(false);
  const [fetching,     setFetching]     = useState(isEdit);
  const [errors,       setErrors]       = useState([]);
  const [aiDescLoading, setAiDescLoading] = useState(false);
  const [aiMsgLoading,  setAiMsgLoading]  = useState(false);

  // ── Itinéraire ────────────────────────────────────────────────
  const [origCountry, setOrigCountry] = useState('');
  const [orig,        setOrig]        = useState('');
  const [destCountry, setDestCountry] = useState('');
  const [dest,        setDest]        = useState('');

  // ── Transport (multi, max 3) ──────────────────────────────────
  const [transports,      setTransports]      = useState(['avion']);
  const [transportCustom, setTransportCustom] = useState('');

  // ── Dates & flexibilité ────────────────────────────────────────
  const [ddep, setDdep] = useState('');
  const [darr, setDarr] = useState('');
  const [flex, setFlex] = useState('fixe');

  // ── Description ────────────────────────────────────────────────
  const [noteTraj, setNoteTraj] = useState('');

  // ── Capacité ──────────────────────────────────────────────────
  const [pds,     setPds]     = useState('');
  const [pdsMax,  setPdsMax]  = useState('');
  const [nbColis, setNbColis] = useState('');
  const [dimL,    setDimL]    = useState('');
  const [dimW,   setDimW]   = useState('');
  const [dimH,   setDimH]   = useState('');
  const [cats,   setCats]   = useState(() => Object.fromEntries(CATEGORIES.map(c => [c, false])));
  const [catCustom, setCatCustom] = useState('');

  // ── Tarification ──────────────────────────────────────────────
  const [txMode,          setTxMode]          = useState('kg');
  const [pKg,             setPKg]             = useState('');
  const [pFixe,           setPFixe]           = useState('');
  const [currencies,      setCurrencies]      = useState(['EUR']);
  const [primaryCurrency, setPrimaryCurrency] = useState('EUR');
  const [counterOffers,   setCounterOffers]   = useState(true);
  const [pkgLimits,       setPkgLimits]       = useState(() => calcPriceLimits('EUR', isPro));

  // Recalcule les bornes prix/kg en devise locale quand la devise principale change.
  // 1) Applique immédiatement les taux statiques (pas de flash EUR).
  // 2) Met à jour avec les taux live si l'API répond.
  useEffect(() => {
    setPkgLimits(calcPriceLimits(primaryCurrency, isPro));
    if (primaryCurrency === 'EUR') return;
    CurrencyService.getRates().then(data => {
      const rate = data?.rates?.[primaryCurrency];   // fix: data.rates.XXX, pas data.XXX
      if (!rate) return;
      const maxEur = isPro ? 1500 : 250;
      setPkgLimits({
        min: ZERO_DEC_CCY.has(primaryCurrency) ? Math.ceil(rate)              : Math.round(rate * 100) / 100,
        max: ZERO_DEC_CCY.has(primaryCurrency) ? Math.floor(rate * maxEur)    : Math.round(rate * maxEur * 100) / 100,
      });
    }).catch(() => {});
  }, [primaryCurrency, isPro]);

  // ── Conditions & message ──────────────────────────────────────
  const [cond,   setCond]   = useState({});
  const [msgExp, setMsgExp] = useState('');

  // ── Chargement edit / duplicate ───────────────────────────────
  useEffect(() => {
    if (isEdit) {
      setFetching(true);
      axios.get(`${API}/trips/${id}`)
        .then(r => {
          const t = r.data;
          setOrigCountry(t.departure_country || '');
          setOrig(t.departure_city || '');
          setDestCountry(t.arrival_country || '');
          setDest(t.arrival_city || '');
          // Trajet expiré : le republier demande une nouvelle date (le serveur refuse l'ancienne)
          setDdep(t.status === 'EXPIRED' ? '' : (t.departure_date?.slice(0, 10) || ''));
          setDarr(t.arrival_date?.slice(0, 10) || '');
          setFlex(t.flex_departure || 'fixe');
          setNoteTraj(t.description?.split(' · ')[0] || '');
          setTransports(t.transport_modes?.length ? t.transport_modes : ['avion']);
          setTransportCustom(t.transport_custom || '');
          setPds(t.available_weight_kg || '');
          setPdsMax(t.max_weight_per_parcel || '');
          setNbColis(t.max_parcels != null ? String(t.max_parcels) : '');
          if (t.max_dimensions) { const [l, w, h] = t.max_dimensions.replace(' cm', '').split('×').map(x => x.trim()); setDimL(l || ''); setDimW(w || ''); setDimH(h || ''); }
          if (t.accepted_categories) {
            const catMap = Object.fromEntries(CATEGORIES.map(c => [c, false]));
            t.accepted_categories.forEach(c => { if (catMap[c] !== undefined) catMap[c] = true; });
            setCats(catMap);
          }
          setCatCustom(t.category_custom || '');
          setTxMode(t.pricing_mode || 'kg');
          setPKg(t.price_per_kg || '');
          setPFixe(t.price_fixed || '');
          setCurrencies(t.currencies?.length ? t.currencies : ['EUR']);
          setPrimaryCurrency(t.primary_currency || 'EUR');
          setCounterOffers(t.counter_offers ?? true);
          if (t.conditions?.length) {
            const condMap = {};
            t.conditions.forEach(c => { condMap[c] = true; });
            setCond(condMap);
          }
          setMsgExp(t.message_to_senders || '');
        })
        .catch(() => toast.error('Trajet introuvable'))
        .finally(() => setFetching(false));
    } else if (isDuplicate) {
      try {
        const stored = sessionStorage.getItem('c2s_trip_duplicate');
        if (stored) {
          const d = JSON.parse(stored);
          setOrigCountry(d.departure_country || '');
          setOrig(d.departure_city || '');
          setDestCountry(d.arrival_country || '');
          setDest(d.arrival_city || '');
          setPds(d.available_weight_kg || '');
          setPdsMax(d.max_weight_per_parcel != null ? String(d.max_weight_per_parcel) : '');
          setNbColis(d.max_parcels != null ? String(d.max_parcels) : '');
          if (d.max_dimensions) { const [l, w, h] = d.max_dimensions.replace(' cm', '').split('×').map(x => x.trim()); setDimL(l || ''); setDimW(w || ''); setDimH(h || ''); }
          setPKg(d.price_per_kg || '');
          setNoteTraj(d.description?.split(' · ')[0] || '');
          setTransports(d.transport_modes?.length ? d.transport_modes : ['avion']);
          sessionStorage.removeItem('c2s_trip_duplicate');
          toast.success('Formulaire prérempli — adaptez les informations avant de publier.');
        }
      } catch { /* sessionStorage indisponible */ }
    }
  }, [id, isEdit, isDuplicate, toast]);

  // ── Transport toggle (max 3) ──────────────────────────────────
  const toggleTransport = (tid) => {
    setTransports(prev => {
      if (prev.includes(tid)) return prev.filter(t => t !== tid);
      if (prev.length >= 3) { toast.error('3 moyens de transport maximum.'); return prev; }
      return [...prev, tid];
    });
  };

  // ── Devise : ajouter / définir primaire / retirer ─────────────
  // Clic sur badge non-sélectionné → ajouter (si < 3)
  // Clic sur badge sélectionné non-primaire → le définir primaire
  // Clic sur badge primaire → rien (doit d'abord en définir un autre)
  // × dans le badge → retirer (seulement si non-primaire)
  const handleCurrencyClick = (code) => {
    if (currencies.includes(code)) {
      if (code !== primaryCurrency) setPrimaryCurrency(code);
    } else {
      if (currencies.length >= 3) { toast.error('3 devises maximum.'); return; }
      setCurrencies(prev => [...prev, code]);
    }
  };

  const removeCurrency = (code) => {
    if (code === primaryCurrency) {
      toast.error('Retirez d\'abord le statut "principale" en choisissant une autre devise.');
      return;
    }
    setCurrencies(prev => prev.filter(c => c !== code));
  };

  // ── Validation ────────────────────────────────────────────────
  const validate = () => {
    const errs = [];
    if (!origCountry) errs.push('Le pays de départ est requis.');
    if (!orig)        errs.push('La ville de départ est requise.');
    if (!destCountry) errs.push("Le pays d'arrivée est requis.");
    if (!dest)        errs.push("La ville d'arrivée est requise.");
    if (origCountry === destCountry && orig && dest && orig.toLowerCase() === dest.toLowerCase())
      errs.push("Le départ et l'arrivée doivent être différents.");
    if (!ddep) errs.push('La date de départ est requise.');
    if (transports.length === 0) errs.push('Sélectionnez au moins un moyen de transport.');
    if (transports.includes('autre') && !transportCustom.trim())
      errs.push('Précisez le moyen de transport "Autre".');
    if (!pds || +pds < 0.1 || +pds > maxWeightAllowed)
      errs.push(`La capacité doit être entre 0,1 et ${maxWeightAllowed} kg.`);
    if (pdsMax && (+pdsMax < 0.1 || +pdsMax > +pds))
      errs.push('Le poids max/colis doit être ≤ à la capacité totale.');
    if (cats['Autre'] && !catCustom.trim())
      errs.push('Précisez la catégorie "Autre".');
    if (txMode === 'kg' && (!pKg || +pKg < pkgLimits.min || +pKg > pkgLimits.max))
      errs.push(`Le tarif ${currencySymbol(primaryCurrency)}/kg doit être entre ${pkgLimits.min.toLocaleString('fr-FR')} et ${pkgLimits.max.toLocaleString('fr-FR')} ${currencySymbol(primaryCurrency)}.`);
    if (txMode === 'fixe' && (!pFixe || +pFixe < pkgLimits.min)) {
      const sym2 = currencySymbol(primaryCurrency);
      const minFmt = pkgLimits.min.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: ZERO_DEC_CCY.has(primaryCurrency) ? 0 : 2 });
      errs.push(`Le prix fixe doit être ≥ ${minFmt} ${sym2}${primaryCurrency !== 'EUR' ? ' (≡ 1 €)' : ''}.`);
    }
    return errs;
  };

  // ── IA — description ──────────────────────────────────────────
  const generateDescription = async () => {
    const transportLabels = transports.map(t =>
      t === 'autre' ? (transportCustom || 'Autre') : (TRANSPORTS.find(tr => tr.id === t)?.label ?? t)
    );
    setAiDescLoading(true);
    try {
      const res = await axios.post(`${API}/ai/trip-description`, {
        origin: orig ? `${orig}, ${origCountry}` : origCountry,
        destination: dest ? `${dest}, ${destCountry}` : destCountry,
        transports: transportLabels,
        departure_date: ddep,
        capacity_kg: pds ? parseFloat(pds) : undefined,
      });
      setNoteTraj((res.data.description ?? '').slice(0, 120));
      toast.success('Description générée ✓');
    } catch {
      const t = transportLabels[0] || 'avion';
      setNoteTraj(`Trajet ${orig || '?'} → ${dest || '?'} par ${t}. Colis pris en charge avec soin.`.slice(0, 120));
    } finally {
      setAiDescLoading(false);
    }
  };

  // ── IA — message aux expéditeurs ──────────────────────────────
  const generateMessage = async () => {
    const condLabels = Object.entries(cond)
      .filter(([, v]) => v)
      .map(([k]) => CONDITIONS.find(c => c.id === k)?.label?.replace(/^\S+\s/, '') ?? k);
    setAiMsgLoading(true);
    try {
      const res = await axios.post(`${API}/ai/trip-message`, {
        origin: orig,
        destination: dest,
        conditions: condLabels,
        pricing_mode: txMode,
        primary_currency: primaryCurrency,
      });
      setMsgExp((res.data.message ?? '').slice(0, 150));
      toast.success('Message généré ✓');
    } catch {
      const condStr = condLabels.slice(0, 3).join(', ');
      setMsgExp((condStr
        ? `Bienvenue ! Pour ce trajet, je demande : ${condStr}. N'hésitez pas à me contacter.`
        : `Bienvenue ! Je transporte vos colis de ${orig} à ${dest} avec soin. Contactez-moi pour tout renseignement.`
      ).slice(0, 150));
    } finally {
      setAiMsgLoading(false);
    }
  };

  // ── Soumission ────────────────────────────────────────────────
  const submit = async () => {
    if (!hasCardPayable(currencies)) {
      toast.error('Ajoutez une devise payable par carte : le cedi seul ne permet pas de conclure.');
      return;
    }
    const errs = validate();
    setErrors(errs);
    if (errs.length) {
      toast.error('Veuillez corriger les champs en rouge.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setLoading(true);
    try {
      const selectedCats = Object.entries(cats).filter(([k, v]) => v && !(k === 'Autre' && catCustom.trim())).map(([k]) => k);
      if (cats['Autre'] && catCustom.trim()) selectedCats.push(catCustom.trim());
      const condList = Object.entries(cond).filter(([, v]) => v).map(([k]) => k);

      // La description est le texte libre de l'auteur : tout le reste a son champ (N4 — plus d'encodage dans le texte)
      const description = noteTraj || `Trajet ${orig} → ${dest}`;

      let pricePerKg = null;
      if (txMode === 'kg') pricePerKg = parseFloat(pKg);
      else if (txMode === 'fixe') pricePerKg = parseFloat(pFixe) / parseFloat(pds);
      else pricePerKg = 0;

      const payload = {
        departure_city: orig,
        arrival_city: dest,
        departure_date: new Date(ddep).toISOString(),
        available_weight_kg: parseFloat(pds),
        price_per_kg: pricePerKg,
        description,
        departure_country: origCountry,
        arrival_country: destCountry,
        transport_modes: transports,
        transport_custom: transportCustom || null,
        flex_departure: flex,
        arrival_date: darr ? new Date(darr).toISOString() : null,
        max_weight_per_parcel: pdsMax ? parseFloat(pdsMax) : null,
        max_parcels: nbColis ? parseInt(nbColis, 10) : null,
        // Dimensions max d'un colis : dans un champ (N4), plus dans la description
        max_dimensions: (dimL || dimW || dimH) ? `${dimL || '?'} × ${dimW || '?'} × ${dimH || '?'} cm` : null,
        accepted_categories: selectedCats,
        category_custom: catCustom || null,
        pricing_mode: txMode,
        price_fixed: txMode === 'fixe' ? parseFloat(pFixe) : null,
        currencies,
        primary_currency: primaryCurrency,
        counter_offers: counterOffers,
        conditions: condList,
        message_to_senders: msgExp || null,
      };

      if (isEdit) {
        await axios.patch(`${API}/trips/${id}`, payload);
        toast.success('Trajet mis à jour — en attente de validation ✓');
        navigate(`/app/trips/${id}`);
      } else {
        const res = await axios.post(`${API}/trips`, payload);
        toast.success('Trajet publié — en attente de validation ✓');
        navigate(`/app/trips/${res.data.id}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur publication');
    } finally {
      setLoading(false);
    }
  };

  // ── Récap sidebar ─────────────────────────────────────────────
  const sym = currencySymbol(primaryCurrency);

  const capDisplay = useMemo(() => (pds ? `${parseFloat(pds).toFixed(1)} kg` : '·'), [pds]);

  const priceDisplay = useMemo(() => {
    if (txMode === 'kg'   && pKg)   return `${parseFloat(pKg).toFixed(ZERO_DEC_CCY.has(primaryCurrency) ? 0 : 2)} ${sym}/kg`;
    if (txMode === 'fixe' && pFixe) return `${parseFloat(pFixe).toFixed(0)} ${sym} fixe`;
    if (txMode === 'libre')         return `Libre (${currencies.join(', ')})`;
    return '·';
  }, [txMode, pKg, pFixe, sym, currencies, primaryCurrency]);

  const route = useMemo(() => {
    const o = orig || (origCountry ? `(${COUNTRIES.find(c => c.code === origCountry)?.name})` : '?');
    const d = dest || (destCountry ? `(${COUNTRIES.find(c => c.code === destCountry)?.name})` : '?');
    return `${o} → ${d}`;
  }, [orig, dest, origCountry, destCountry]);

  const calcExample = useMemo(() => {
    if (txMode === 'kg'   && pKg && pds) return `Pour ${pds} kg : ${(parseFloat(pKg) * parseFloat(pds)).toFixed(ZERO_DEC_CCY.has(primaryCurrency) ? 0 : 2)} ${sym}`;
    if (txMode === 'fixe' && pFixe)      return `Prix fixe : ${parseFloat(pFixe).toFixed(0)} ${sym} / colis`;
    return '';
  }, [txMode, pKg, pFixe, pds, sym, primaryCurrency]);

  const hasError = (kind) => errors.some(e => {
    if (kind === 'orig_country') return e.includes('pays de départ');
    if (kind === 'orig_city')    return e.includes('ville de départ');
    if (kind === 'dest_country') return e.includes("pays d'arrivée");
    if (kind === 'dest_city')    return e.includes("ville d'arrivée") || e.includes('différents');
    if (kind === 'date')         return e.includes('date');
    if (kind === 'transport')    return e.includes('transport');
    if (kind === 'cap')          return e.includes('capacité');
    if (kind === 'pdsMax')       return e.includes('max/colis');
    if (kind === 'pkg')          return e.includes('/kg');
    if (kind === 'pfixe')        return e.includes('fixe');
    if (kind === 'catCustom')    return e.includes('catégorie');
    return false;
  });

  if (fetching) return <div style={{ padding: 32, color: 'var(--ink3)' }}>Chargement…</div>;

  if (!isEdit && user && !canDo(user, 'publish')) {
    return (
      <div className="sf-wrap">
        <div className="sf-head">
          <div className="sf-head-title">Publier un trajet</div>
        </div>
        <VerificationGate user={user} action="publish" goal="publier un trajet" />
      </div>
    );
  }

  return (
    <div className="sf-wrap">
      <div className="sf-head">
        <div>
          <div className="sf-head-title">
            {isEdit ? 'Modifier le trajet' : isDuplicate ? 'Dupliquer un trajet' : 'Publier un trajet'}
          </div>
          <div className="sf-head-sub">
            {isPro ? '✔ Abonné PRO — jusqu\'à 1 500 kg' : (
              <>
                Jusqu'à 250 kg (1 500 kg avec{' '}
                <a className="pro-upsell" href="/app/abonnement" target="_blank" rel="noopener noreferrer" data-testid="trip-form-pro-upsell">
                  l'abonnement PRO
                </a>)
              </>
            )}
            {isDuplicate && ' · Formulaire prérempli — adaptez avant de publier.'}
          </div>
        </div>
        <button type="button" className="sf-btn sf-btn-ghost" style={{ width: 'auto' }}
          onClick={() => navigate('/app/trips')} data-testid="btn-cancel">Annuler</button>
      </div>

      <div className="sf-grid">
        <div className="sf-main">
          {errors.length > 0 && (
            <div className="sf-err-box" data-testid="error-box">
              <strong>Quelques champs nécessitent votre attention :</strong>
              <ul>{errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}

          {/* ── Section 1 : Itinéraire & transport ─────────────── */}
          <div className="sf-card">
            <div className="sf-card-hd">
              <span className="sf-step-badge">1</span>
              Itinéraire &amp; transport
            </div>
            <div className="sf-card-bd">
              <div className="sf-row">
                <CountryCitySelector
                  dirLabel="Départ"
                  country={origCountry}
                  onCountryChange={setOrigCountry}
                  city={orig}
                  onCityChange={setOrig}
                  errCountry={hasError('orig_country')}
                  errCity={hasError('orig_city')}
                />
                <CountryCitySelector
                  dirLabel="Arrivée"
                  country={destCountry}
                  onCountryChange={setDestCountry}
                  city={dest}
                  onCityChange={setDest}
                  errCountry={hasError('dest_country')}
                  errCity={hasError('dest_city')}
                />
              </div>

              <div className="sf-row">
                <div className="sf-field">
                  <label className="sf-label">Date de départ <span className="sf-req">*</span></label>
                  <input type="date" className={`sf-input ${hasError('date') ? 'err' : ''}`}
                    value={ddep} onChange={e => setDdep(e.target.value)}
                    min={new Date().toISOString().slice(0, 10)} data-testid="input-date-dep" />
                </div>
                <div className="sf-field">
                  <label className="sf-label">
                    Date d'arrivée estimée
                    {flex !== 'fixe' && (
                      <span className="sf-flex-badge">
                        ⏳ {flex === 'souple' ? '± 3 jours' : 'Flexible'}
                      </span>
                    )}
                  </label>
                  <input type="date" className="sf-input"
                    value={darr} onChange={e => setDarr(e.target.value)} min={ddep || undefined} />
                </div>
              </div>

              <div className="sf-field">
                <label className="sf-label">
                  Moyen(s) de transport <span className="sf-req">*</span>
                  <span style={{ fontWeight: 400, color: 'var(--ink3)', marginLeft: 6 }}>
                    ({transports.length}/3)
                  </span>
                </label>
                <div className="sf-icon-grid">
                  {TRANSPORTS.map(t => {
                    const isOn      = transports.includes(t.id);
                    const isDisabled = !isOn && transports.length >= 3;
                    return (
                      <div
                        key={t.id}
                        className={`sf-icon-c ${isOn ? 'on' : ''} ${isDisabled ? 'disabled' : ''}`}
                        onClick={() => !isDisabled && toggleTransport(t.id)}
                        role="checkbox" aria-checked={isOn} tabIndex={0}
                        onKeyDown={e => e.key === 'Enter' && !isDisabled && toggleTransport(t.id)}
                        data-testid={`transport-${t.id}`}
                      >
                        <span className="sf-icon-glyph" aria-hidden="true">{t.glyph}</span>
                        <span>{t.label}</span>
                      </div>
                    );
                  })}
                </div>
                {hasError('transport') && (
                  <div style={{ color: '#d92d20', fontSize: 12, marginTop: 4 }}>
                    Sélectionnez au moins un moyen de transport.
                  </div>
                )}
                {transports.includes('autre') && (
                  <div className="sf-custom-input">
                    <input type="text" className="sf-input"
                      placeholder='Précisez (ex. "Moto", "Camion frigorifique"…)'
                      value={transportCustom} onChange={e => setTransportCustom(e.target.value)} maxLength={60} />
                  </div>
                )}
              </div>

              <div className="sf-field">
                <label className="sf-label">Flexibilité de date</label>
                <div className="sf-seg">
                  <button type="button" className={`sf-seg-btn ${flex === 'fixe'   ? 'on' : ''}`} onClick={() => setFlex('fixe')}>📅 Date fixe</button>
                  <button type="button" className={`sf-seg-btn ${flex === 'souple' ? 'on' : ''}`} onClick={() => setFlex('souple')}>⏳ ± 3 jours</button>
                  <button type="button" className={`sf-seg-btn ${flex === 'libre'  ? 'on' : ''}`} onClick={() => setFlex('libre')}>✨ Flexible</button>
                </div>
              </div>

              <div className="sf-field">
                <label className="sf-label">Description du trajet</label>
                <div className="sf-ai-row">
                  <textarea className="sf-textarea" rows={3}
                    placeholder="Ex. Vol direct CDG → DSS, disponible pour remise Paris 12e…"
                    value={noteTraj} onChange={e => setNoteTraj(e.target.value.slice(0, 120))}
                    data-testid="input-description" />
                  <button type="button" className="sf-btn-ai" onClick={generateDescription}
                    disabled={aiDescLoading} title="Générer avec l'IA">
                    {aiDescLoading ? '…' : '✨ IA'}
                  </button>
                </div>
                <div className="sf-char-count">{noteTraj.length}/120</div>
              </div>
            </div>
          </div>

          {/* ── Section 2 : Capacité ─────────────────────────────── */}
          <div className="sf-card">
            <div className="sf-card-hd"><span className="sf-step-badge">2</span>Capacité disponible</div>
            <div className="sf-card-bd">
              <div className="sf-row">
                <div className="sf-field">
                  <label className="sf-label">Poids disponible (kg) <span className="sf-req">*</span></label>
                  <input type="number" min="0.1" step="0.5" placeholder="15"
                    className={`sf-input ${hasError('cap') ? 'err' : ''}`}
                    value={pds} onChange={e => setPds(e.target.value)} data-testid="input-capacity" />
                  <div className="sf-hint">
                    Entre 0,1 et {maxWeightAllowed} kg{isPro ? ' (abonné PRO)' : ''}.
                    {!isPro && (
                      <>
                        {' '}Plus lourd ?{' '}
                        <a className="pro-upsell" href="/app/abonnement" target="_blank" rel="noopener noreferrer">Passez PRO</a> (jusqu'à 1 500 kg).
                      </>
                    )}
                  </div>
                </div>
                <div className="sf-field">
                  <label className="sf-label">Poids max / colis (kg)</label>
                  <input type="number" min="0.1" step="0.5" placeholder={pds || '8'}
                    className={`sf-input ${hasError('pdsMax') ? 'err' : ''}`}
                    value={pdsMax} onChange={e => setPdsMax(e.target.value)} />
                  <div className="sf-hint">Facultatif. Limite individuelle par colis.</div>
                </div>
                <div className="sf-field">
                  <label className="sf-label">Nbre de colis max</label>
                  <input type="number" min="1" step="1" placeholder="Ex : 5"
                    className="sf-input"
                    value={nbColis} onChange={e => setNbColis(e.target.value)} />
                  <div className="sf-hint">Facultatif. Nombre total de colis acceptés.</div>
                </div>
              </div>

              <div className="sf-field">
                <label className="sf-label">Dimensions max (cm)</label>
                <div className="sf-dims">
                  <input type="number" min="1" placeholder="L" className="sf-input"
                    value={dimL} onChange={e => setDimL(e.target.value)} aria-label="Longueur max" />
                  <input type="number" min="1" placeholder="l" className="sf-input"
                    value={dimW} onChange={e => setDimW(e.target.value)} aria-label="Largeur max" />
                  <input type="number" min="1" placeholder="H" className="sf-input"
                    value={dimH} onChange={e => setDimH(e.target.value)} aria-label="Hauteur max" />
                </div>
              </div>

              <div className="sf-field">
                <label className="sf-label">Catégories acceptées</label>
                <div className="sf-checks">
                  {CATEGORIES.map(c => (
                    <label key={c} className={`sf-check ${c === 'Autre' ? 'full' : ''}`}>
                      <input type="checkbox" checked={!!cats[c]}
                        onChange={e => {
                          if (e.target.checked && Object.values(cats).filter(Boolean).length >= 5) {
                            toast.error('5 catégories maximum.'); return;
                          }
                          setCats(x => ({ ...x, [c]: e.target.checked }));
                        }} />
                      <span>{c}</span>
                    </label>
                  ))}
                </div>
                {cats['Autre'] && (
                  <div className="sf-custom-input">
                    <input type="text"
                      className={`sf-input ${hasError('catCustom') ? 'err' : ''}`}
                      placeholder='Précisez (ex. "Pièces détachées", "Jouets"…)'
                      value={catCustom} onChange={e => setCatCustom(e.target.value)} maxLength={60} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Section 3 : Tarification ─────────────────────────── */}
          <div className="sf-card">
            <div className="sf-card-hd"><span className="sf-step-badge">3</span>Tarification</div>
            <div className="sf-card-bd">
              <div className="sf-field">
                <label className="sf-label">Mode de tarification <span className="sf-req">*</span></label>
                <div className="sf-seg">
                  <button type="button" className={`sf-seg-btn ${txMode === 'kg'    ? 'on' : ''}`} onClick={() => setTxMode('kg')}>⚖️ Au kg</button>
                  <button type="button" className={`sf-seg-btn ${txMode === 'fixe'  ? 'on' : ''}`} onClick={() => setTxMode('fixe')}>🏷️ Fixe</button>
                  <button type="button" className={`sf-seg-btn ${txMode === 'libre' ? 'on' : ''}`} onClick={() => setTxMode('libre')}>💬 Libre</button>
                </div>
                <div className="sf-hint">
                  {txMode === 'kg'    && 'Prix calculé selon le poids réel de chaque colis.'}
                  {txMode === 'fixe'  && 'Prix unique par colis, peu importe le poids.'}
                  {txMode === 'libre' && 'Pas de prix affiché — vous négocierez directement avec chaque expéditeur.'}
                </div>
              </div>

              {txMode === 'kg' && (
                <div className="sf-field">
                  <label className="sf-label">Tarif ({sym}/kg) <span className="sf-req">*</span></label>
                  <input type="number" min={pkgLimits.min} step="1" placeholder={String(Math.round(pkgLimits.min * 3))}
                    className={`sf-input ${hasError('pkg') ? 'err' : ''}`}
                    value={pKg} onChange={e => setPKg(e.target.value)} data-testid="input-price-kg" />
                  <div className="sf-hint">
                    Entre {pkgLimits.min.toLocaleString('fr-FR')} et {pkgLimits.max.toLocaleString('fr-FR')} {sym}/kg
                    {primaryCurrency !== 'EUR' && <span style={{ color:'var(--ink3)', marginLeft:4 }}>(≡ 1–{isPro?1500:250} €)</span>}
                  </div>
                </div>
              )}

              {txMode === 'fixe' && (
                <div className="sf-field">
                  <label className="sf-label">Prix fixe / colis ({sym}) <span className="sf-req">*</span></label>
                  <input type="number" min={pkgLimits.min} step="1" placeholder={String(Math.max(30, Math.round(pkgLimits.min * 3)))}
                    className={`sf-input ${hasError('pfixe') ? 'err' : ''}`}
                    value={pFixe} onChange={e => setPFixe(e.target.value)} data-testid="input-price-fixe" />
                  <div className="sf-hint">
                    Minimum {pkgLimits.min.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: ZERO_DEC_CCY.has(primaryCurrency) ? 0 : 2 })} {sym}
                    {primaryCurrency !== 'EUR' && <span style={{ color: 'var(--ink3)', marginLeft: 4 }}>(≡ 1 €)</span>}
                  </div>
                </div>
              )}

              <div className="sf-field">
                <label className="sf-label">
                  Devise(s) acceptée(s)
                  <span style={{ fontWeight: 400, color: 'var(--ink3)', marginLeft: 6 }}>
                    ({currencies.length}/3 · ★ = principale · clic pour choisir · × pour retirer)
                  </span>
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                  {CURRENCIES_LIST.map(cur => {
                    const isSelected = currencies.includes(cur.code);
                    const isPrimary  = cur.code === primaryCurrency;
                    const isDisabled = !isSelected && currencies.length >= 3;
                    return (
                      <button key={cur.code} type="button"
                        className={`sf-badge ${isPrimary ? 'primary' : isSelected ? 'secondary' : ''}`}
                        style={isDisabled ? { opacity: .4, cursor: 'not-allowed' } : {}}
                        onClick={() => !isDisabled && handleCurrencyClick(cur.code)}
                        title={
                          isPrimary ? `${cur.name} — devise principale` :
                          isSelected ? `Cliquer pour définir ${cur.code} comme principale` :
                          `Ajouter ${cur.name}`
                        }
                      >
                        {isPrimary ? '★ ' : ''}{cur.symbol} {cur.code}
                        {isSelected && !isPrimary && (
                          <span
                            role="button"
                            aria-label={`Retirer ${cur.code}`}
                            style={{ marginLeft: 5, fontWeight: 800, fontSize: 12, lineHeight: 1, opacity: .7 }}
                            onClick={e => { e.stopPropagation(); removeCurrency(cur.code); }}
                          >×</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {!hasCardPayable(currencies) && (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#b45309' }} data-testid="sf-no-card-payable">
                    Le cedi ne peut pas être payé par carte : il sert à afficher votre prix. Ajoutez une devise payable
                    (par exemple l'euro ou le franc CFA) pour que l'on puisse vous faire une proposition.
                  </div>
                )}
                <div className="sf-hint">
                  Cliquez sur une devise pour l'ajouter. Cliquez sur une devise sélectionnée pour la définir comme principale (★).
                </div>
              </div>

              <div className="sf-field">
                <label className="sf-label">Négociation</label>
                <div className="sf-seg">
                  <button type="button" className={`sf-seg-btn ${counterOffers ? 'on' : ''}`}
                    onClick={() => setCounterOffers(true)}>💬 Contre-offres acceptées</button>
                  {txMode !== 'libre' && (
                    <button type="button" className={`sf-seg-btn ${!counterOffers ? 'on' : ''}`}
                      onClick={() => setCounterOffers(false)}>🔒 Prix ferme</button>
                  )}
                </div>
                {txMode === 'libre' && (
                  <div className="sf-hint">En mode Libre, les contre-offres sont le mécanisme de fixation du prix. Pas de prix ferme possible.</div>
                )}
              </div>

              {calcExample && (
                <div className="sf-info" data-testid="calc-example">💡 {calcExample}</div>
              )}
            </div>
          </div>

          {/* ── Section 4 : Conditions ────────────────────────────── */}
          <div className="sf-card">
            <div className="sf-card-hd"><span className="sf-step-badge">4</span>Conditions d'acceptation</div>
            <div className="sf-card-bd">
              <div className="sf-checks">
                {CONDITIONS.map(c => (
                  <label key={c.id} className="sf-check">
                    <input type="checkbox" checked={!!cond[c.id]}
                      onChange={e => {
                        if (e.target.checked && Object.values(cond).filter(Boolean).length >= 5) {
                          toast.error('5 conditions maximum.'); return;
                        }
                        setCond(x => ({ ...x, [c.id]: e.target.checked }));
                      }}
                      data-testid={`condition-${c.id}`} />
                    <span>{c.label}</span>
                  </label>
                ))}
              </div>

              <div className="sf-field" style={{ marginTop: 16 }}>
                <label className="sf-label">Message aux expéditeurs</label>
                <div className="sf-ai-row">
                  <textarea className="sf-textarea" rows={3}
                    placeholder="Informations complémentaires, disponibilités de contact…"
                    value={msgExp} onChange={e => setMsgExp(e.target.value.slice(0, 150))}
                    maxLength={150} />
                  <div className="sf-char-count">{msgExp.length}/150</div>
                  <button type="button" className="sf-btn-ai" onClick={generateMessage}
                    disabled={aiMsgLoading} title="Générer avec l'IA">
                    {aiMsgLoading ? '…' : '✨ IA'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Récap sidebar ──────────────────────────────────────── */}
        <aside className="sf-aside">
          <div className="sf-recap">
            <div className="sf-recap-hero">
              <div className="sf-recap-eyebrow">Publication transporteur</div>
              <div className="sf-recap-title">{noteTraj ? noteTraj.slice(0, 48) : 'Nouveau trajet'}</div>
              <div className="sf-recap-route">{route}</div>
            </div>

            <div className="sf-recap-stats">
              <div className="sf-recap-stat">
                <div className="sf-recap-stat-label">Capacité</div>
                <div className="sf-recap-stat-value" data-testid="recap-capacity">{capDisplay}</div>
              </div>
              <div className="sf-recap-stat">
                <div className="sf-recap-stat-label">Tarif</div>
                <div className="sf-recap-stat-value" data-testid="recap-price">{priceDisplay}</div>
              </div>
            </div>

            <div className="sf-recap-body">
              {transports.length > 0 && (
                <div className="sf-recap-line">
                  <span className="k">Transport</span>
                  <span className="v">
                    {transports.map(t => TRANSPORTS.find(tr => tr.id === t)?.glyph ?? '').join(' ')}
                    {' '}{transports.map(t =>
                      t === 'autre' ? (transportCustom || 'Autre') : (TRANSPORTS.find(tr => tr.id === t)?.label ?? t)
                    ).join(', ')}
                  </span>
                </div>
              )}
              <div className="sf-recap-line">
                <span className="k">Départ</span>
                <span className="v">{ddep ? new Date(ddep).toLocaleDateString('fr-FR') : '·'}</span>
              </div>
              {darr && (
                <div className="sf-recap-line">
                  <span className="k">Arrivée</span>
                  <span className="v">{new Date(darr).toLocaleDateString('fr-FR')}</span>
                </div>
              )}
              {flex !== 'fixe' && (
                <div className="sf-recap-line">
                  <span className="k">Flexibilité</span>
                  <span className="v">{flex === 'souple' ? '± 3 jours' : 'Flexible'}</span>
                </div>
              )}
              {pdsMax && (
                <div className="sf-recap-line">
                  <span className="k">Max/colis</span>
                  <span className="v">{pdsMax} kg</span>
                </div>
              )}
              {nbColis && (
                <div className="sf-recap-line">
                  <span className="k">Colis max</span>
                  <span className="v">{nbColis} colis</span>
                </div>
              )}
              {currencies.length > 1 && (
                <div className="sf-recap-line">
                  <span className="k">Devises</span>
                  <span className="v">{currencies.map(c => c === primaryCurrency ? `★ ${c}` : c).join(', ')}</span>
                </div>
              )}
              {txMode === 'libre' && (
                <div className="sf-recap-line">
                  <span className="k">Contre-offres</span>
                  <span className="v">{counterOffers ? 'Oui' : 'Non'}</span>
                </div>
              )}
            </div>

            <div className="sf-escrow">
              <span aria-hidden="true">🛡️</span>
              <span>Votre trajet sera visible par les expéditeurs sur les corridors compatibles dès validation.</span>
            </div>

            <div className="sf-actions">
              <button type="button" className="sf-btn sf-btn-primary" onClick={submit}
                disabled={loading} data-testid="btn-submit">
                {loading ? 'Publication…' : (isEdit ? 'Mettre à jour le trajet' : 'Publier le trajet')}
              </button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
